import {
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { FC, ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { ROUTES } from 'constants/routes';
import slug from 'constants/slug';
import useTranslate from 'hooks/useTranslate';
import { getAuthState } from 'redux/auth/selector';
import { companyDetails } from 'redux/retailers/selector';
import { useAppSelector } from 'redux/redux';

interface NavItem {
  to: string;
  labelKey: string;
  icon: ReactNode;
  /** only render for admins */
  adminOnly?: boolean;
  /** only render for the Miele flavour */
  mieleOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    to: `/${ROUTES.DASHBOARD}/${ROUTES.RETAILERS}`,
    labelKey: 'REFERRER_MANAGEMENT',
    icon: <PeopleOutlineIcon />,
    adminOnly: true,
  },
  {
    to: `/${ROUTES.DASHBOARD}/${ROUTES.POLICIES_LEDGER}`,
    labelKey: 'POLICIES_LEDGER',
    icon: <ReceiptLongIcon />,
    adminOnly: true,
    mieleOnly: true,
  },
];

/** styled NavLink so MUI ListItemButton can consume the router active state */
const NavItemLink = styled(NavLink)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

interface SidebarProps {
  open: boolean;
  /** close handler — used by the temporary (mobile) drawer */
  onClose: () => void;
}

const Sidebar: FC<SidebarProps> = ({ open, onClose }) => {
  const { parseUI } = useTranslate();
  const { isAdmin } = useAppSelector(getAuthState);
  const companyData = useAppSelector(companyDetails);
  const { pathname } = useLocation();
  const theme = useTheme();
  /** desktop = persistent (pushes content); mobile/tablet = temporary (overlay) */
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const items = NAV_ITEMS.filter((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.mieleOnly && companyData?.slug !== slug.MIELE) return false;
    return true;
  });

  return (
    <Drawer
      variant={isDesktop ? 'persistent' : 'temporary'}
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={(theme) => ({
        width: theme.sidebar.width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: theme.sidebar.width,
          boxSizing: 'border-box',
          background: theme.sidebar.background,
          color: theme.sidebar.textColor,
          borderRight: `1px solid ${theme.sidebar.dividerBg}`,
          boxShadow: theme.sidebar.boxShadow,
        },
      })}
    >
      {/* spacer matching the fixed header height so the menu sits below the logo */}
      <Toolbar
        sx={(theme) => ({ minHeight: `${theme.header.height} !important` })}
      />
      <Divider sx={(theme) => ({ borderColor: theme.sidebar.dividerBg })} />

      <Box sx={{ overflowY: 'auto', py: 1 }}>
        <List
          component="nav"
          aria-label={parseUI('POLICIES_LEDGER')}
          subheader={
            <ListSubheader
              component="div"
              disableSticky
              sx={(theme) => ({
                background: 'transparent',
                color: theme.sidebar.menuItemHeadingColor,
                fontWeight: 700,
                fontSize: 12,
                letterSpacing: 0.5,
                textTransform: 'uppercase',
                lineHeight: '40px',
              })}
            >
              {parseUI('MENU')}
            </ListSubheader>
          }
        >
          {items.map((item) => {
            const selected = pathname.startsWith(item.to);

            return (
              <NavItemLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  /** auto-close the overlay drawer after navigating on mobile */
                  if (!isDesktop) onClose();
                }}
              >
                <ListItemButton
                  selected={selected}
                  sx={(theme) => ({
                    mx: 1.5,
                    my: 0.5,
                    borderRadius: theme.general.borderRadius,
                    color: theme.sidebar.menuItemColor,
                    '& .MuiListItemIcon-root': {
                      color: theme.sidebar.menuItemIconColor,
                      minWidth: 40,
                    },
                    '&:hover': {
                      background: theme.sidebar.menuItemBgActive,
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                      background: theme.sidebar.menuItemBgActive,
                      color: theme.sidebar.menuItemColorActive,
                      '& .MuiListItemIcon-root': {
                        color: theme.sidebar.menuItemIconColorActive,
                      },
                    },
                  })}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={parseUI(item.labelKey)}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
                  />
                </ListItemButton>
              </NavItemLink>
            );
          })}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
